import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import translations from "./translations";

export const LANGUAGES = [
    { code: "hy", label: "ՀԱՅ", name: "Հայերեն" },
    { code: "en", label: "ENG", name: "English" },
    { code: "ru", label: "РУС", name: "Русский" },
];

const STORAGE_KEY = "ctrl_lang";
const DEFAULT_LANG = "en";

const LanguageContext = createContext({
    lang: DEFAULT_LANG,
    setLang: () => {},
    t: (key) => key,
});

const getInitialLang = () => {
    if (typeof window === "undefined") return DEFAULT_LANG;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return LANGUAGES.some((l) => l.code === stored) ? stored : DEFAULT_LANG;
};

export const LanguageProvider = ({ children }) => {
    const [lang, setLangState] = useState(getInitialLang);

    useEffect(() => {
        document.documentElement.lang = lang;
        try {
            window.localStorage.setItem(STORAGE_KEY, lang);
        } catch {
            /* ignore storage failures */
        }
    }, [lang]);

    const setLang = useCallback((code) => {
        if (LANGUAGES.some((l) => l.code === code)) setLangState(code);
    }, []);

    // Fall back to Armenian, then to the raw key, when a translation is empty.
    // Handles strings and arrays (an empty array counts as "not translated").
    const t = useCallback(
        (key) => {
            const isEmpty = (v) =>
                v == null ||
                v === "" ||
                (Array.isArray(v) && v.length === 0);

            const value = (translations[lang] || {})[key];
            if (!isEmpty(value)) return value;

            const fallback = translations[DEFAULT_LANG][key];
            return fallback == null ? key : fallback;
        },
        [lang]
    );

    const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useTranslation = () => useContext(LanguageContext);

export default LanguageContext;
